// Deterministic node-graph geometry shared by the hero and the "How We Work"
// section. Nodes sit on a jittered grid so they cover the full area without
// being perfectly even, and edge nodes spill past the viewBox so the graph
// appears to continue beyond every side. Same seed => same graph every render.

export interface GraphNode {
  x: number;
  y: number;
  r: number;
}

export interface GraphLink {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Proximity 0..1 (1 = touching), used to fade distant links. */
  o: number;
}

export interface Graph {
  W: number;
  H: number;
  nodes: GraphNode[];
  links: GraphLink[];
}

export interface BuildGraphOptions {
  width?: number;
  height?: number;
  cols?: number;
  rows?: number;
  seed?: number;
  /** Base node radius; each node adds up to `radiusJitter` on top. */
  radius?: number;
  radiusJitter?: number;
}

export const buildGraph = ({
  width = 1200,
  height = 600,
  cols = 13,
  rows = 7,
  seed = 0x1a2b3c4d,
  radius = 1,
  radiusJitter = 1,
}: BuildGraphOptions = {}): Graph => {
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const cellW = width / (cols - 1);
  const cellH = height / (rows - 1);

  const nodes: GraphNode[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      nodes.push({
        x: col * cellW + (rand() - 0.5) * cellW * 0.95,
        y: row * cellH + (rand() - 0.5) * cellH * 0.95,
        r: radius + rand() * radiusJitter,
      });
    }
  }

  const maxDist = Math.min(cellW, cellH) * 1.55;
  const links: GraphLink[] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
      if (d < maxDist) {
        links.push({
          x1: nodes[i].x,
          y1: nodes[i].y,
          x2: nodes[j].x,
          y2: nodes[j].y,
          o: 1 - d / maxDist,
        });
      }
    }
  }

  return { W: width, H: height, nodes, links };
};
